import { useAttachments } from '@/features/conversation/queries/attachments.queries';
import MediaItem from '@/features/messages/components/media-layout/media-item';
import { useDownload } from '@/features/messages/hooks/use-download';
import { useAttachmentUrl } from '@/features/messages/queries/use-attachment-url';
import { Download } from '@mui/icons-material';
import Masonry from '@mui/lab/Masonry';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PhotoProvider } from 'react-photo-view';
import { AttachmentType } from '../../../messages/models/attachment';
import EmptyState from './empty-state';
import ErrorState from './error-state';
import LoadingState from './loading-state';

interface MediaTabPanelProps {
  conversationId: string;
}

interface AttachmentItemRaw {
  messageId?: string;
  id?: string;
  attachment: {
    contentType: AttachmentType;
    url?: string;
    filename?: string;
    [key: string]: unknown;
  };
}

interface MediaItemWithDownloadProps {
  item: AttachmentItemRaw;
  isHovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const MediaItemWithDownload: FC<MediaItemWithDownloadProps> = ({ item, isHovered, onMouseEnter, onMouseLeave }) => {
  const { blobUrl } = useAttachmentUrl(item.attachment.url);
  const { mutate: downloadFile, isPending } = useDownload(
    item.attachment.url || '',
    item.attachment.filename || 'media-file'
  );

  // Use blobUrl if available, otherwise fallback to original url
  const displayUrl = blobUrl || item.attachment.url;

  return (
    <div
      className='mex:group mex:relative mex:border-2 mex:border-gray-300'
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <MediaItem
        attachment={item.attachment as any}
        alt={item.attachment.filename || 'Media attachment'}
        objectFit='cover'
      />

      {/* Overlay with buttons on hover */}
      {isHovered && displayUrl && (
        <Tooltip title='Tải xuống'>
          <IconButton
            className='mex:absolute mex:top-2 mex:right-2 mex:z-10 mex:bg-black/40'
            size='small'
            onClick={() => downloadFile()}
            disabled={isPending}
          >
            {isPending ? (
              <CircularProgress size={16} className='mex:text-white' />
            ) : (
              <Download className='mex:text-white' />
            )}
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
};

export const MediaTabPanel: FC<MediaTabPanelProps> = ({ conversationId }) => {
  const { t } = useTranslation();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const { files, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } = useAttachments({
    conversationId,
    contentType: AttachmentType.MEDIA,
    limit: 20
  });

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState />;
  if (!files.length) return <EmptyState />;

  return (
    <PhotoProvider>
      <Masonry columns={2} spacing={0.5} className='mex:px-2' component='div'>
        {files.map((item) => {
          const att = item as AttachmentItemRaw;
          const itemId = att.messageId || att.id || String(Math.random());
          const isHovered = hoveredItem === itemId;

          return (
            <MediaItemWithDownload
              key={itemId}
              item={att}
              isHovered={isHovered}
              onMouseEnter={() => setHoveredItem(itemId)}
              onMouseLeave={() => setHoveredItem(null)}
            />
          );
        })}
      </Masonry>

      {hasNextPage && !isFetchingNextPage && (
        <Box className='mex:pt-2 mex:text-center'>
          <Button
            variant='text'
            size='small'
            onClick={() => fetchNextPage()}
            className='mex:!text-blue-500 mex:hover:!text-blue-700'
          >
            Xem thêm
          </Button>
        </Box>
      )}

      {isFetchingNextPage && (
        <Box className='mex:pt-2 mex:text-center'>
          <Typography variant='body2' className='mex:text-gray-500'>
            {t('conversations.loading.default')}
          </Typography>
        </Box>
      )}
    </PhotoProvider>
  );
};
